import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {AuthInputErrorNotification} from '@Components/auth-input/components/auth-input-error-notification';
import {PASSWORD_STATUS} from '@Components/signup-form/signup-form.types';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {VoidFunction} from '@Components/login-page/login-page.types';
import {noop} from '@Utils/general.util';
import {INPUT_FIELD_TYPE, InputField} from '../input-field';
import styles from './auth-input.module.scss';
import {useIsLoginPageFieldErrorActive} from '@Components/login-page/login-page.hooks';
import type {AuthTestId} from '@/test-ids/auth-test-id';

type HandleInputType = ((txt: string) => void) | VoidFunction;

interface AuthInputProps {
  label: string;
  placeholder: string;
  type: INPUT_FIELD_TYPE;
  onInputSubmit: VoidFunction;
  handleInput: HandleInputType;
  showError?: boolean;
  onPasswordIconEyeClick?: VoidFunction;
  passwordFieldIcon?: string;
  isInvalid?: boolean;
  errorText?: string;
  onFocusCallback?: VoidFunction;
  onFocusOutCallback?: VoidFunction;
  passwordStatus?: PASSWORD_STATUS;
  disabled?: boolean;
  enableFocusStyles?: boolean;
  isLabelResponsive?: boolean;
  value?: string;
  errorTextContainsHtml?: boolean;
  id?: string;
  'data-testid'?: AuthTestId;
  errorTestId?: AuthTestId;
  iconTestId?: AuthTestId;
}

function AuthInput({
  label,
  placeholder,
  type,
  handleInput,
  onInputSubmit,
  showError = false,
  onPasswordIconEyeClick = noop,
  passwordFieldIcon = '',
  errorText = '',
  onFocusCallback = noop,
  onFocusOutCallback = noop,
  passwordStatus = PASSWORD_STATUS.DEFAULT,
  disabled = false,
  enableFocusStyles = false,
  isInvalid = false,
  isLabelResponsive = true,
  value = '',
  errorTextContainsHtml = false,
  ...props
}: AuthInputProps): ReactElement<any> {
  const [hasFocus, setHasFocus] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPageErrorActive = useIsLoginPageFieldErrorActive();

  const getInputFieldClass = (): string => {
    if (type === INPUT_FIELD_TYPE.EMAIL) {
      return styles.emailField;
    }

    if (type === INPUT_FIELD_TYPE.PASSWORD) {
      return styles.passwordField;
    }
    return '';
  };

  /**
   * While a field is focused its focus ring replaces the error styling, so the message has to go with
   * it — otherwise a user correcting their input sees a focused field still captioned as invalid. Both
   * the border and the message read this, so they cannot drift apart.
   */
  const isFocusStyleActive = (): boolean => {
    return enableFocusStyles && hasFocus;
  };

  const getErrorNotification = (): ReactElement | null => {
    if (!showError || isFocusStyleActive()) {
      return null;
    }
    return <AuthInputErrorNotification dangerouslySetInnerHTML={errorTextContainsHtml} errorText={errorText} data-testid={props.errorTestId} />;
  };

  const getClassNames = (): string => {
    let borderClass = 'border-s-dark';

    if (type === INPUT_FIELD_TYPE.PASSWORD && passwordStatus === PASSWORD_STATUS.ERROR) {
      borderClass = styles.borderDanger;
    }


    if (showError || isInvalid || (isPageErrorActive && type === INPUT_FIELD_TYPE.EMAIL)) {
      borderClass = styles.borderDanger;
    }

    if (isFocusStyleActive()) {
      borderClass = styles.borderFocus;
    }

    return `content-body-white ${borderClass} ${styles.inputField} ${getInputFieldClass()} ${disabled ? styles.disabledField : ''} ${isLabelResponsive ? '' : 'spacing-m-t-1'}`;
  };

  const onInputChange = (txt: string): void => {
    handleInput(txt);
  };

  const onFocus = (): void => {
    setHasFocus(true);
  };

  const onBlur = (): void => {
    setHasFocus(false);
  };

  /**
   * Submitting with Enter leaves the field focused, and the focus ring outranks the error styling — so
   * a failed submit would render as focused rather than invalid. Dropping focus on submit lets the
   * error state show, matching what clicking the button already does.
   */
  const onAuthInputSubmit = (): void => {
    if (enableFocusStyles) {
      containerRef.current?.querySelector('input')?.blur();
    }
    onInputSubmit();
  };

  return (
    <div className={styles.authInputContainer} ref={containerRef}>
      <label htmlFor={props.id} className={`content-body body-md-bold ${isLabelResponsive ? styles.label : ''}`}>
        {label}
      </label>

      <div onFocus={onFocusCallback} onBlur={onFocusOutCallback} className={`flexbox ${styles.inputFieldContainer}`}>
        <InputField
          id={props.id}
          placeholder={placeholder}
          className={`${getClassNames()} spacing-p-0 ${styles.iconContainer}`}
          type={type}
          onInputChange={onInputChange}
          onInputIconClick={onPasswordIconEyeClick}
          inputIconSize={IconSize.SIZE_ICON_16}
          inputIconExtraClass={styles.icon}
          showInputIcon={type !== INPUT_FIELD_TYPE.EMAIL}
          aria-label={label}
          data-testid={props['data-testid']}
          inputIconTestId={props.iconTestId}
          inputIconName={passwordFieldIcon}
          inputClassName="spacing-p-3"
          onInputSubmit={onAuthInputSubmit}
          inputIconType={IconType.NONE}
          disabled={disabled}
          onInputFocus={onFocus}
          onInputBlur={onBlur}
          value={value}
        />
      </div>

      {getErrorNotification()}
    </div>
  );
}

export default React.memo(AuthInput);
